/**
 * MI Register — P17-UAT MI Workflow Consolidation (register-only).
 *
 * The authoritative Material Issue ENTRY POINT is now the MIR Processing
 * Request (Purchase & Inventory → Material Issue Request). This screen is a
 * transaction REGISTER only: search / view / print / reverse / audit posted
 * Material Issues. It no longer creates or posts an MI.
 *
 * The newly consolidated split architecture is preserved end-to-end:
 *   ONE RM Item + ONE Batch/Lot = ONE MI Number.
 *
 * MI Reverse dialog carries only { reason, confirmation_id } — D-ε1 bans
 * Restore Reservation / Allocation / Replan / PR-adjustment knobs.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  PackageIcon, ArrowsClockwiseIcon, PrinterIcon, ArrowUUpLeftIcon,
} from "@phosphor-icons/react";
import RequiredVsActual from "@/components/RequiredVsActual";
import useConfirmationId from "@/hooks/useConfirmationId";
import { RowActions } from "@/components/RowActions";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const num = (v) => Number(v || 0);

export default function MaterialIssueMI() {
  const [mis, setMis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMi, setViewMi] = useState(null);
  const [srcFilter, setSrcFilter] = useState("all");
  const [reverseTarget, setReverseTarget] = useState(null);          // {mi, preview}

  const filteredMis = useMemo(() => {
    if (srcFilter === "all") return mis;
    return mis.filter(m => (m.issue_source || (m.auto_generated ? "Automated" : "Manual")) === srcFilter);
  }, [mis, srcFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const { data: history } = await api.get("/material-issues/");
      setMis(history || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load MI Register");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openPrint = async (mi_id) => {
    try {
      const res = await api.get(`/material-issues/${mi_id}/print`, { responseType: "text" });
      const w = window.open("", "_blank");
      if (!w) { toast.error("Enable pop-ups to preview the Material Issue Note"); return; }
      w.document.open(); w.document.write(res.data); w.document.close();
      setTimeout(() => { try { w.focus(); w.print(); } catch (_) {} }, 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Print failed");
    }
  };

  // ── Reverse-MI dialog · H1-ε · D-η7 ────────────────────────────
  const openReverseDialog = async (mi) => {
    try {
      const { data } = await api.get(`/material-issues/${mi.id}/reverse-preview`);
      setReverseTarget({ mi, preview: data, reason: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reversal preview");
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="material-issue-mi-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><PackageIcon size={11} weight="bold" />Purchase &amp; Inventory · MI Register</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">MI Register</h1>
          <div className="text-xs text-zinc-500 mt-1">Transaction register — search, view, print, reverse &amp; audit posted Material Issues. Issues are created from the <span className="text-[var(--accent)]">Material Issue Request → Processing Request</span>. Split rule preserved: <span className="font-mono text-[var(--accent)]">1 RM Item + 1 Batch/Lot = 1 MI</span>.</div>
        </div>
        <button onClick={load} disabled={loading} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm rounded-sm flex items-center gap-2" data-testid="mi-refresh">
          <ArrowsClockwiseIcon size={14} weight="bold" />Refresh
        </button>
      </div>

      {/* Posted Material Issues · register (read/audit only) */}
      <div className="tactical-card overflow-x-auto" data-testid="mi-history">
        <div className="p-4 border-b border-[var(--border)] text-sm font-semibold flex items-center justify-between flex-wrap gap-2">
          <span>Posted Material Issues ({filteredMis.length}{srcFilter !== "all" ? ` of ${mis.length}` : ""})</span>
          <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest">
            <span className="text-zinc-500 mr-1">Source:</span>
            {[
              { k: "all", label: "All", cls: "border-zinc-500/40 text-zinc-300" },
              { k: "Automated", label: "🤖 Auto", cls: "border-sky-500/40 text-sky-300" },
              { k: "Manual", label: "👤 Manual", cls: "border-amber-500/40 text-amber-300" },
            ].map(b => (
              <button key={b.k} data-testid={`mi-src-filter-${b.k}`}
                      onClick={() => setSrcFilter(b.k)}
                      className={`px-2 py-1 rounded-sm border ${srcFilter === b.k ? `${b.cls} bg-white/5` : "border-[var(--border)] text-zinc-500 hover:text-zinc-300"}`}>
                {b.label}
              </button>
            ))}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
            <tr>
              <th className="text-left px-5 py-3">MI No</th>
              <th className="text-left px-5 py-3">Source</th>
              <th className="text-left px-5 py-3">Linked MIR</th>
              <th className="text-left px-5 py-3">Dept</th>
              <th className="text-left px-5 py-3">RM Item</th>
              <th className="text-left px-5 py-3">Batch/Lot</th>
              <th className="text-right px-5 py-3">Qty Issued</th>
              <th className="text-left px-5 py-3">Date</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-right px-5 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredMis.length === 0 && <tr><td colSpan="10" className="text-center px-5 py-8 text-zinc-500">{srcFilter === "all" ? "No Material Issues posted yet" : `No ${srcFilter} MIs found`}</td></tr>}
            {filteredMis.map(r => {
              const lines = r.lines || [];
              const totalQty = lines.reduce((s, l) => s + num(l.qty), 0);
              const isReversed = (r.status || "") === "Reversed";
              // P17-UAT MI-REGISTER · show the ACTUAL issued RM item. Leg-aware MIs
              // keep the Required→Actual chip; MIs persisted with only line-level
              // rm_code (no legs) fall back to the actual issued rm_code. Multi-item
              // MIs list each actual issued item within the same MI row.
              const rmItems = [];
              const seen = new Set();
              lines.forEach(ln => {
                const legs = ln.legs || [];
                if (legs.length) {
                  legs.forEach(lg => {
                    const key = `${ln.required_rm_code}→${lg.actual_rm_code}`;
                    if (!seen.has(key)) { seen.add(key); rmItems.push({ type: "leg", req: ln.required_rm_code, act: lg.actual_rm_code, at: lg.allocation_type }); }
                  });
                } else if (ln.rm_code) {
                  const key = `plain:${ln.rm_code}`;
                  if (!seen.has(key)) { seen.add(key); rmItems.push({ type: "plain", code: ln.rm_code }); }
                }
              });
              // P17-UAT MI Phase-2/3 · batch/lot identity per MI row.
              const batchList = [];
              const seenB = new Set();
              lines.forEach(ln => {
                const src = (ln.legs || []).length
                  ? (ln.legs || []).flatMap(lg => lg.batches || [])
                  : (ln.batches || []);
                src.forEach(b => {
                  const id = b.batch_no || b.lot_no || b.lot_id || "";
                  if (id && !seenB.has(id)) { seenB.add(id); batchList.push(id); }
                });
              });
              return (
                <tr key={r.id} className={`border-b border-[var(--border)] last:border-b-0 ${isReversed ? "opacity-60" : ""}`} data-testid={`mi-hist-${r.mi_no}`}>
                  <td className="px-5 py-3 font-mono font-bold text-emerald-300">{r.mi_no}</td>
                  <td className="px-5 py-3">
                    <span data-testid={`mi-src-${r.mi_no}`}
                          className={`inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${(r.issue_source === "Automated") ? "bg-sky-500/15 text-sky-300 border-sky-500/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}`}>
                      {(r.issue_source === "Automated") ? "🤖 Auto" : "👤 Manual"}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-mono text-[11px] text-zinc-300">{r.mrq_no || "—"}</td>
                  <td className="px-5 py-3 text-[11px]">{r.department || "—"}</td>
                  <td className="px-5 py-3">
                    {rmItems.length === 0 && <span className="text-zinc-500 text-[11px] italic" title="Item identity unavailable">Item identity unavailable</span>}
                    <div className="flex flex-col gap-1">
                      {rmItems.slice(0, 3).map((it, i) => (
                        it.type === "leg"
                          ? <RequiredVsActual key={i} requiredCode={it.req} actualCode={it.act} allocationType={it.at} />
                          : <span key={i} data-testid={`mi-rm-${r.mi_no}`} className="font-mono text-[11px] text-white break-all leading-snug">{it.code}</span>
                      ))}
                      {rmItems.length > 3 && (
                        <span className="text-[10px] text-zinc-500 font-mono">+{rmItems.length - 3} more</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3" data-testid={`mi-batch-${r.mi_no}`}>
                    {batchList.length === 0
                      ? <span className="text-zinc-500">—</span>
                      : <div className="flex flex-col gap-1">
                          {batchList.slice(0, 3).map((b, i) => (
                            <span key={i} className="font-mono text-[11px] text-sky-300 break-all leading-snug">{b}</span>
                          ))}
                          {batchList.length > 3 && <span className="text-[10px] text-zinc-500 font-mono">+{batchList.length - 3} more</span>}
                        </div>}
                  </td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums font-semibold text-white">{fmt(totalQty)}</td>
                  <td className="px-5 py-3 text-[11px] text-zinc-400">{(r.issued_at || "").slice(0, 10)}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${isReversed ? "bg-red-500/10 text-red-300 border-red-500/40" : "bg-emerald-500/10 text-emerald-300 border-emerald-500/40"}`}
                          data-testid={`mi-status-${r.mi_no}`}>
                      {isReversed ? "Reversed" : "Posted"}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <RowActions
                      testid={`mi-actions-${r.mi_no}`}
                      items={[
                        { label: "View", testid: `mi-view-${r.mi_no}`, onClick: () => setViewMi(r) },
                        { label: "Print", tone: "approve", testid: `mi-print-${r.mi_no}`, onClick: () => openPrint(r.id) },
                        { label: "Reverse", tone: "reverse", testid: `mi-reverse-${r.mi_no}`, show: !isReversed, onClick: () => openReverseDialog(r) },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Reverse MI modal · H1-ε · D-η7 */}
      <Dialog open={!!reverseTarget} onOpenChange={(o) => !o && setReverseTarget(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-3xl max-h-[92vh] overflow-y-auto"
                       data-testid="mi-reverse-dialog"
                       description="Reverse the entire Material Issue · physical restoration only.">
          {reverseTarget && (
            <ReverseMiDialog
              target={reverseTarget}
              onCancel={() => setReverseTarget(null)}
              onDone={() => { setReverseTarget(null); load(); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* MI detail dialog · read-only */}
      <Dialog open={!!viewMi} onOpenChange={(o) => !o && setViewMi(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="mi-view-dialog"
                       description="Material Issue detail · read-only view of the posted physical issue.">
          {viewMi && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-lg flex items-center gap-2">
                  <span className="font-mono text-emerald-300">{viewMi.mi_no}</span>
                  <span className="text-zinc-500 text-xs">·</span>
                  <span className="text-zinc-400 text-xs uppercase tracking-widest">Material Issue Detail</span>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px] font-mono mt-2">
                <div><div className="text-zinc-500 mb-0.5">Linked MIR</div><div className="text-white">{viewMi.mrq_no || "—"}</div></div>
                <div><div className="text-zinc-500 mb-0.5">Dept</div><div className="text-white">{viewMi.department || "—"}</div></div>
                <div><div className="text-zinc-500 mb-0.5">Source Godown</div><div className="text-amber-300">{viewMi.source_godown_code || "—"}</div></div>
                <div><div className="text-zinc-500 mb-0.5">Destination Godown</div><div className="text-emerald-300" data-testid={`mi-view-dest-${viewMi.mi_no}`}>{viewMi.dest_godown_code || "—"}</div></div>
              </div>
              <div className="border border-[var(--border)] rounded-sm overflow-hidden mt-3">
                <table className="w-full text-xs">
                  <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="px-3 py-2 text-left w-8">#</th>
                      <th className="px-3 py-2 text-left">Required → Actual · Allocation</th>
                      <th className="px-3 py-2 text-right w-28">Qty</th>
                      <th className="px-3 py-2 text-left">Batches</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewMi.lines || []).flatMap((ln, li) =>
                      (ln.legs || [{ actual_rm_code: ln.rm_code, qty: ln.qty, batches: ln.batches, allocation_type: "EXACT" }]).map((lg, gi) => (
                        <tr key={`${li}-${gi}`} className="border-t border-[var(--border)]">
                          <td className="px-3 py-2 font-mono text-zinc-500">{li + 1}.{gi + 1}</td>
                          <td className="px-3 py-2">
                            <RequiredVsActual
                              requiredCode={ln.required_rm_code || ln.rm_code}
                              actualCode={lg.actual_rm_code}
                              allocationType={lg.allocation_type || "EXACT"}
                              alternateApprovalNo={lg.alternate_approval_id ? "yes" : ""}
                              alternatePriority={lg.alternate_priority}
                            />
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums text-white font-semibold">{fmt(lg.qty)}</td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-1">
                              {(lg.batches || []).length === 0 && <span className="text-zinc-500">—</span>}
                              {(lg.batches || []).map((b, j) => (
                                <span key={j} className="inline-flex flex-col items-start px-1.5 py-0.5 rounded-sm border border-purple-500/30 bg-purple-500/5">
                                  <span className="font-mono text-[10px] text-emerald-300">{b.lot_no || b.batch_no || "—"}</span>
                                  {b.batch_no && <span className="font-mono text-[9px] text-purple-300">Batch: {b.batch_no}</span>}
                                  <span className="font-mono text-[9px] text-zinc-400">{fmt(b.qty)}</span>
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <DialogFooter className="pt-2">
                <button onClick={() => setViewMi(null)} className="px-3 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400" data-testid="mi-view-close">Close</button>
                <button onClick={() => openPrint(viewMi.id)} data-testid="mi-view-print"
                        className="px-4 py-2 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center gap-2">
                  <PrinterIcon size={14} weight="bold" />Print MI Note
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════
// Reverse MI sub-dialog · H1-ε · D-η7 · NO restore-* knobs anywhere
// ══════════════════════════════════════════════════════════════════
function ReverseMiDialog({ target, onCancel, onDone }) {
  const { mi, preview } = target;
  const [reason, setReason] = useState("");
  const blocking = preview.blocking_reason || "";
  const fingerprint = useMemo(() => `${mi.id}|${reason.trim()}`, [mi.id, reason]);
  const { confirmation_id, arm, succeed, settle, disable_submit } = useConfirmationId({ fingerprint });

  const submit = async () => {
    if (reason.trim().length < 4) { toast.error("Reason must be at least 4 characters"); return; }
    const conf = arm();
    try {
      const { data } = await api.post(`/material-issues/${mi.id}/reverse`, {
        reason: reason.trim(),
        confirmation_id: conf,
      });
      toast.success(`MI ${data.mi_no} reversed · ${(data.reversed_movement_ids || []).length} V2 movements`);
      succeed();
      onDone();
    } catch (e) {
      settle();
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : "";
      if (code === "IDEMPOTENCY_CONFLICT") toast.error("Idempotency conflict · refresh and retry");
      else if (code === "IDEMPOTENT_REPLAY_IN_FLIGHT") toast.warning("Prior reversal still processing");
      else if (code === "MI_ALREADY_REVERSED") toast.info("This MI is already reversed.");
      else toast.error(typeof detail === "string" ? detail : (detail?.message || "Reversal failed"));
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display flex items-center gap-2">
          <ArrowUUpLeftIcon size={18} weight="bold" className="text-red-300" />
          Reverse MI · Emergency Physical Issue Correction
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-4 mt-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px] font-mono">
          <div><div className="text-zinc-500 mb-0.5">MI No</div><div className="text-emerald-300">{preview.mi_no}</div></div>
          <div><div className="text-zinc-500 mb-0.5">MRQ</div><div className="text-white">{preview.mrq_no || "—"}</div></div>
          <div><div className="text-zinc-500 mb-0.5">Expected reversal legs</div><div className="text-white">{preview.expected_reversal_legs}</div></div>
        </div>
        <div className="border border-[var(--border)] rounded-sm overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-[var(--bg-2)]/40 text-[9px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left w-10">Leg</th>
                <th className="px-3 py-2 text-left">Required → Actual · Allocation</th>
                <th className="px-3 py-2 text-right w-24">Return Qty</th>
                <th className="px-3 py-2 text-left">Batches</th>
              </tr>
            </thead>
            <tbody>
              {(preview.per_leg || []).map((lg, i) => (
                <tr key={i} className="border-t border-[var(--border)]">
                  <td className="px-3 py-2 font-mono text-zinc-500">#{lg.leg_ordinal}</td>
                  <td className="px-3 py-2">
                    <RequiredVsActual
                      requiredCode={lg.required_rm_code}
                      actualCode={lg.actual_rm_code}
                      allocationType={lg.allocation_type}
                      alternateApprovalNo={lg.alternate_approval_id ? "yes" : ""}
                    />
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-white">{fmt(lg.qty)}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(lg.batches || []).map((b, j) => (
                        <span key={j} className="inline-flex flex-col items-start px-1.5 py-0.5 rounded-sm border border-purple-500/30 bg-purple-500/5">
                          <span className="font-mono text-[10px] text-purple-300">{b.batch_no || "—"}</span>
                          <span className="font-mono text-[9px] text-zinc-400">{fmt(b.qty)}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {blocking && (
          <div className="text-[11px] font-mono px-3 py-2 rounded-sm border border-red-500/60 bg-red-500/10 text-red-300"
                data-testid="mi-reverse-blocked">
            {blocking}
          </div>
        )}
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1">Reversal Reason * (min 4 chars)</label>
          <textarea rows={3} data-testid="mi-reverse-reason"
                     value={reason} onChange={e => setReason(e.target.value)}
                     placeholder="e.g. Wrong batch keyed. Restoring RM-B batch B07 and rolling MRQ.qty_issued back."
                     className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono" />
        </div>
        <div className="text-[10px] font-mono text-zinc-500 border-t border-[var(--border)] pt-2 leading-relaxed"
              data-testid="mi-reverse-copy">
          Emergency physical issue correction only — planning / reservations are unchanged. After reversal, physical stock is restored to the Actual RM + exact batch/lot, and MRQ.qty_issued rolls back against the Required RM only.
        </div>
      </div>
      <DialogFooter>
        <div className="mr-auto text-[10px] font-mono text-zinc-500">
          {confirmation_id ? <>UUID armed · <span className="text-purple-300">{confirmation_id.slice(0, 8)}</span></> : "UUID mints on Reverse"}
        </div>
        <button onClick={onCancel} className="px-3 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400" data-testid="mi-reverse-cancel">Cancel</button>
        <button onClick={submit} disabled={!!blocking || disable_submit}
                data-testid="mi-reverse-submit"
                className="px-4 py-2 rounded-sm bg-red-600 hover:bg-red-700 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-40">
          <ArrowUUpLeftIcon size={14} weight="bold" />
          {disable_submit ? "Reversing…" : "Reverse Entire MI"}
        </button>
      </DialogFooter>
    </>
  );
}
